import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api, { scheduleSilentRefresh, clearSilentRefresh, refreshAuthSession } from '@/api/client'

import type { AssignedAgency } from '@/types/dashboard'

export interface User {
  id: string
  username: string
  email: string
  first_name?: string
  last_name?: string
  roles: string[]
  permissions: string[]
  groups?: string[]
  assignedAgencies?: AssignedAgency[]
  officeId?: string | number
  office_id?: string | number | null
  officeName?: string
  office_name?: string | null
  territorial_scope?: 'all' | 'local' | string
}

export interface MenuItem {
  id: string
  label: string
  icon: string
  path?: string
  permissions: string[]
  children?: MenuItem[]
}

const ROLE_PERMISSIONS_MAP: Record<string, string[]> = {
  PLATFORM_SUPERADMIN: ['*'],
  SuperAdmin: ['*'],
  isp_super_admin: ['*'],
  isp_admin: ['*'],
  admin: ['*'],
  TENANT_OWNER: ['*'],
  SUPERADMIN: ['*'],
  Owner: ['*'],
  owner: ['*'],
  CEO: ['*'],
  CFO: ['*'],
  COO: ['*'],
  DIRECTOR: ['*'],
  GERENTE_GENERAL: ['*'],
  EJECUTIVO: ['*'],
  TEAM_LEADER: ['*'],
  team_leader: ['*'],
  'Team Leader': ['*'],
  Team_Leader: ['*'],
  isp_team_leader: ['*'],
  LIDER: ['*'],
  LIDER_TECNICO: ['*'],
  SUPERVISOR: ['*'],
  AdminSucursal: [
    'customers:*',
    'billing:read',
    'billing:invoices:read',
    'billing:payments:read',
    'billing:void_local',
    'helpdesk:*',
    'tasks:assign_local',
    'reports:office',
  ],
  Cajero: [
    'billing:invoices:read',
    'billing:payments:create',
    'billing:payments:write',
    'billing:receipts:print',
    'customers:read',
  ],
  SoporteN1: [
    'helpdesk:chats:claim',
    'helpdesk:chats:reply',
    'helpdesk:console:read',
    'helpdesk:chat:read',
    'helpdesk:chat:write',
    'customers:read',
    'tasks:tickets:create',
  ],
  SoporteN2: [
    'network:*',
    'monitor:*',
    'helpdesk:*',
    'support:*',
    'customers:*',
    'tasks:dispatch:*',
  ],
  Vendedor: [
    'sales:leads:*',
    'sales:contracts:create',
    'sales:orders:*',
    'crm:leads:*',
    'customers:create',
    'customers:read',
  ],
  TecnicoCampo: [
    'tasks:assigned:read',
    'tasks:assigned:complete',
    'tasks:field:read',
    'tasks:field:update',
    'erp:inventory:assigned_read',
  ],
  isp_sales_admin: ['sales.*', 'crm.*', 'customers.*'],
  isp_sales_rep: [
    'sales.orders.*',
    'sales.goals.read',
    'sales.subscriptions.*',
    'sales.sellers.read',
    'sales.commissions.read',
    'customers.read',
  ],
  isp_sales_viewer: ['sales.*.read', 'crm.*.read', 'customers.read'],
  isp_billing_admin: ['billing.*', 'customers.*'],
  BILLING_MANAGER: ['billing.*', 'customers.*', 'reports.*', 'erp.*', 'dashboard.view_widgets'],
  FINANCIAL_MANAGER: ['billing.*', 'customers.*', 'reports.*', 'erp.*', 'dashboard.view_widgets'],
  Contador: ['billing.*', 'customers.read', 'reports.*', 'erp.*', 'dashboard.view_widgets'],
  ERP_MANAGER: ['erp.*', 'customers.*', 'reports.*', 'dashboard.view_widgets'],
  isp_billing_operator: ['billing.payments.*', 'billing.invoices.*', 'customers.read'],
  isp_billing_viewer: ['billing.*.read', 'customers.read'],
  isp_reports_admin: ['reports.*'],
  isp_reports_viewer: ['reports.*.read'],
  isp_erp_admin: ['erp.*', 'customers.read'],
  isp_erp_operator: ['erp.inventory.*', 'erp.warehouse.*', 'erp.equipment.*', 'erp.operations.*', 'customers.read'],
  isp_erp_viewer: ['erp.inventory.read', 'erp.warehouse.read', 'erp.equipment.read', 'erp.operations.read', 'customers.read'],
  isp_whatsapp_admin: ['helpdesk.*', 'customers.read'],
  isp_noc_senior: [
    'network.*',
    'monitor.*',
    'support.*',
    'helpdesk.*',
    'customers.*',
    'tasks.dispatch.triage',
    'tasks.field.read',
    'media.*',
    'netplay.*',
    'reports.network.*',
    'dashboard.view_widgets',
  ],
  NOC: [
    'network.*',
    'monitor.*',
    'support.*',
    'helpdesk.*',
    'customers.*',
    'tasks.dispatch.triage',
    'tasks.field.read',
    'media.*',
    'netplay.*',
    'reports.network.*',
    'dashboard.view_widgets',
  ],
  NOC_SENIOR: [
    'network.*',
    'monitor.*',
    'support.*',
    'helpdesk.*',
    'customers.*',
    'tasks.dispatch.triage',
    'tasks.field.read',
    'media.*',
    'netplay.*',
    'reports.network.*',
    'dashboard.view_widgets',
  ],
  isp_noc_l1: [
    'helpdesk.console.read',
    'helpdesk.tickets.*',
    'helpdesk.templates.read',
    'support.tickets.*',
    'customers.read',
    'network.*.read',
    'monitor.*.read',
    'dashboard.view_widgets',
  ],
  NOC_JR: [
    'helpdesk.console.read',
    'helpdesk.tickets.*',
    'helpdesk.templates.read',
    'support.tickets.*',
    'customers.read',
    'network.*.read',
    'monitor.*.read',
    'dashboard.view_widgets',
  ],
  NOC_L1: [
    'helpdesk.console.read',
    'helpdesk.tickets.*',
    'helpdesk.templates.read',
    'support.tickets.*',
    'customers.read',
    'network.*.read',
    'monitor.*.read',
    'dashboard.view_widgets',
  ],
  isp_network_admin: ['network.*', 'monitor.*', 'customers.read'],
  isp_support_admin: ['support.*', 'helpdesk.*', 'customers.read', 'tasks.dispatch.triage'],
  isp_whatsapp_operator: ['helpdesk.console.read', 'helpdesk.tickets.*', 'customers.read'],
  isp_tasks_admin: ['tasks.*', 'customers.read'],
  isp_fiber_technician: [
    'network.fiber.read',
    'network.fiber.write',
    'network.ont.*',
    'network.ont.provision',
    'network.ont.read',
    'network.ont.write',
    'network.devices.read',
    'network.*.read',
    'monitor.alerts.read',
    'monitor.dashboard.read',
    'monitor.devices.read',
    'monitor.*.read',
    'tasks.*',
  ],
  isp_iptv_admin: ['media.*', 'netplay.*', 'ott.*'],
  isp_customer_manager: ['customers.*'],
  AGENCY_MANAGER: [
    'billing.payments.read',
    'billing.invoices.read',
    'customers.read',
    'customers.suspended.read',
    'crm.leads.read',
    'crm.quotes.read',
    'sales.plans.read',
    'tasks.field.read',
    'support.tickets.read',
    'erp.warehouses.manage_stock',
    'dashboard.view_widgets',
    'helpdesk.*',
    'helpdesk.console.read',
    'helpdesk.tickets.*',
    'helpdesk.templates.read',
    'support.*',
  ],
  JEFE_AGENCIA: [
    'billing.payments.read',
    'billing.invoices.read',
    'customers.read',
    'customers.suspended.read',
    'crm.leads.read',
    'crm.quotes.read',
    'sales.plans.read',
    'tasks.field.read',
    'support.tickets.read',
    'erp.warehouses.manage_stock',
    'dashboard.view_widgets',
    'helpdesk.*',
    'helpdesk.console.read',
    'helpdesk.tickets.*',
    'helpdesk.templates.read',
    'support.*',
  ],
  HELPDESK_MANAGER: [
    'helpdesk.*',
    'helpdesk.console.read',
    'helpdesk.tickets.*',
    'helpdesk.templates.read',
    'support.*',
    'customers.read',
    'dashboard.view_widgets',
  ],
  SUPPORT_MANAGER: [
    'helpdesk.*',
    'support.*',
    'customers.read',
    'tasks.dispatch.triage',
    'dashboard.view_widgets',
  ],
  'helpdesk.*': [
    'helpdesk.*',
    'helpdesk.console.read',
    'helpdesk.tickets.*',
    'helpdesk.templates.read',
    'customers.read',
    'dashboard.view_widgets',
  ],
  'Gerente de Ventas': [
    'billing.payments.read',
    'billing.invoices.read',
    'customers.read',
    'customers.suspended.read',
    'crm.leads.read',
    'crm.quotes.read',
    'sales.plans.read',
    'tasks.field.read',
    'support.tickets.read',
    'erp.warehouses.manage_stock',
    'dashboard.view_widgets',
  ],
  EJECUTIVA_VENTAS: [
    'sales.*',
    'crm.*',
    'customers.read',
    'helpdesk.console.read',
    'helpdesk.tickets.*',
    'dashboard.view_widgets',
  ],
  EJECUTIVO_VENTAS: [
    'sales.*',
    'crm.*',
    'customers.read',
    'helpdesk.console.read',
    'helpdesk.tickets.*',
    'dashboard.view_widgets',
  ],
  Facturacion: [
    'billing.*',
    'customers.*',
    'reports.*',
    'helpdesk.console.read',
    'helpdesk.tickets.*',
    'dashboard.view_widgets',
  ],
}

export const BRANCH_MANAGERS_MAP: Record<string, AssignedAgency[]> = {
  // Graciela Henríquez: San Vicente (#4) y San Sebastián (#7)
  'gracielahenriquez@flynet.sv': [
    { id: '4', uuid: '5679595d-c411-49e2-9032-7ec6a12703dc', name: 'Sucursal San Vicente', code: 'SV', city: 'San Vicente', isPrimary: true },
    { id: '7', uuid: 'b69a0e36-f7f1-4b20-8456-dd9c842c9601', name: 'Sucursal San Sebastián', code: 'SS', city: 'San Sebastián' }
  ],
  'graciela.henriquez@flynetwifi.com': [
    { id: '4', uuid: '5679595d-c411-49e2-9032-7ec6a12703dc', name: 'Sucursal San Vicente', code: 'SV', city: 'San Vicente', isPrimary: true },
    { id: '7', uuid: 'b69a0e36-f7f1-4b20-8456-dd9c842c9601', name: 'Sucursal San Sebastián', code: 'SS', city: 'San Sebastián' }
  ],
  'graciela.henriquez@flynet.sv': [
    { id: '4', uuid: '5679595d-c411-49e2-9032-7ec6a12703dc', name: 'Sucursal San Vicente', code: 'SV', city: 'San Vicente', isPrimary: true },
    { id: '7', uuid: 'b69a0e36-f7f1-4b20-8456-dd9c842c9601', name: 'Sucursal San Sebastián', code: 'SS', city: 'San Sebastián' }
  ],
  'gracielahenriquez': [
    { id: '4', uuid: '5679595d-c411-49e2-9032-7ec6a12703dc', name: 'Sucursal San Vicente', code: 'SV', city: 'San Vicente', isPrimary: true },
    { id: '7', uuid: 'b69a0e36-f7f1-4b20-8456-dd9c842c9601', name: 'Sucursal San Sebastián', code: 'SS', city: 'San Sebastián' }
  ],
  'graciela.henriquez': [
    { id: '4', uuid: '5679595d-c411-49e2-9032-7ec6a12703dc', name: 'Sucursal San Vicente', code: 'SV', city: 'San Vicente', isPrimary: true },
    { id: '7', uuid: 'b69a0e36-f7f1-4b20-8456-dd9c842c9601', name: 'Sucursal San Sebastián', code: 'SS', city: 'San Sebastián' }
  ],
  '197': [
    { id: '4', uuid: '5679595d-c411-49e2-9032-7ec6a12703dc', name: 'Sucursal San Vicente', code: 'SV', city: 'San Vicente', isPrimary: true },
    { id: '7', uuid: 'b69a0e36-f7f1-4b20-8456-dd9c842c9601', name: 'Sucursal San Sebastián', code: 'SS', city: 'San Sebastián' }
  ],
  'f1d3f840-2066-4d45-8305-dbbcd1ec7ab9': [
    { id: '4', uuid: '5679595d-c411-49e2-9032-7ec6a12703dc', name: 'Sucursal San Vicente', code: 'SV', city: 'San Vicente', isPrimary: true },
    { id: '7', uuid: 'b69a0e36-f7f1-4b20-8456-dd9c842c9601', name: 'Sucursal San Sebastián', code: 'SS', city: 'San Sebastián' }
  ],
  'c0326084-90aa-43ff-bf31-6450631ecf04': [
    { id: '4', uuid: '5679595d-c411-49e2-9032-7ec6a12703dc', name: 'Sucursal San Vicente', code: 'SV', city: 'San Vicente', isPrimary: true },
    { id: '7', uuid: 'b69a0e36-f7f1-4b20-8456-dd9c842c9601', name: 'Sucursal San Sebastián', code: 'SS', city: 'San Sebastián' }
  ],
  '7c463c92-e60c-47f1-a3af-ddeeb7341e76': [
    { id: '4', uuid: '5679595d-c411-49e2-9032-7ec6a12703dc', name: 'Sucursal San Vicente', code: 'SV', city: 'San Vicente', isPrimary: true },
    { id: '7', uuid: 'b69a0e36-f7f1-4b20-8456-dd9c842c9601', name: 'Sucursal San Sebastián', code: 'SS', city: 'San Sebastián' }
  ],
  'f1d3f840-7ab5-4676-92c2-8406f527c7cf': [
    { id: '4', uuid: '5679595d-c411-49e2-9032-7ec6a12703dc', name: 'Sucursal San Vicente', code: 'SV', city: 'San Vicente', isPrimary: true },
    { id: '7', uuid: 'b69a0e36-f7f1-4b20-8456-dd9c842c9601', name: 'Sucursal San Sebastián', code: 'SS', city: 'San Sebastián' }
  ],
  // Stefany Díaz: San Vicente (#4)
  'stefanydiaz@flynet.sv': [
    { id: '4', uuid: '5679595d-c411-49e2-9032-7ec6a12703dc', name: 'Sucursal San Vicente', code: 'SV', city: 'San Vicente', isPrimary: true }
  ],
  'stefanydiaz': [
    { id: '4', uuid: '5679595d-c411-49e2-9032-7ec6a12703dc', name: 'Sucursal San Vicente', code: 'SV', city: 'San Vicente', isPrimary: true }
  ],
  '93f21789-94b5-4285-84d0-9b044b506a80': [
    { id: '4', uuid: '5679595d-c411-49e2-9032-7ec6a12703dc', name: 'Sucursal San Vicente', code: 'SV', city: 'San Vicente', isPrimary: true }
  ],
  // Verenice Lozano: San Sebastián (#7)
  'verenicelozano@flynet.sv': [
    { id: '7', uuid: 'b69a0e36-f7f1-4b20-8456-dd9c842c9601', name: 'Sucursal San Sebastián', code: 'SS', city: 'San Sebastián', isPrimary: true }
  ],
  'verenicelozano': [
    { id: '7', uuid: 'b69a0e36-f7f1-4b20-8456-dd9c842c9601', name: 'Sucursal San Sebastián', code: 'SS', city: 'San Sebastián', isPrimary: true }
  ],
  '7eaf789f-f664-4860-a2f1-adeb9cb47d53': [
    { id: '7', uuid: 'b69a0e36-f7f1-4b20-8456-dd9c842c9601', name: 'Sucursal San Sebastián', code: 'SS', city: 'San Sebastián', isPrimary: true }
  ],
  // Marcela Flores: Usulután (#3)
  'marcelaflores@flynet.sv': [
    { id: '3', uuid: '4f2cc0b8-8a49-40c2-92ec-6818358032c5', name: 'Sucursal Usulután', code: 'US', city: 'Usulután', isPrimary: true }
  ],
  'marcelaflores': [
    { id: '3', uuid: '4f2cc0b8-8a49-40c2-92ec-6818358032c5', name: 'Sucursal Usulután', code: 'US', city: 'Usulután', isPrimary: true }
  ],
  '174': [
    { id: '3', uuid: '4f2cc0b8-8a49-40c2-92ec-6818358032c5', name: 'Sucursal Usulután', code: 'US', city: 'Usulután', isPrimary: true }
  ],
  // Rolando Morazán / Rolando Chica: Morazán (#2)
  'rolandomorazan@flynet.sv': [
    { id: '2', uuid: 'fb9159ae-c414-4603-9ed5-64f1cce7ae56', name: 'Sucursal Morazán', code: 'MZ', city: 'Morazán', isPrimary: true }
  ],
  'rolandomorazan': [
    { id: '2', uuid: 'fb9159ae-c414-4603-9ed5-64f1cce7ae56', name: 'Sucursal Morazán', code: 'MZ', city: 'Morazán', isPrimary: true }
  ],
  '214': [
    { id: '2', uuid: 'fb9159ae-c414-4603-9ed5-64f1cce7ae56', name: 'Sucursal Morazán', code: 'MZ', city: 'Morazán', isPrimary: true }
  ],
  'rolandochica@flynet.sv': [
    { id: '2', uuid: 'fb9159ae-c414-4603-9ed5-64f1cce7ae56', name: 'Sucursal Morazán', code: 'MZ', city: 'Morazán', isPrimary: true }
  ],
  '172': [
    { id: '2', uuid: 'fb9159ae-c414-4603-9ed5-64f1cce7ae56', name: 'Sucursal Morazán', code: 'MZ', city: 'Morazán', isPrimary: true }
  ],
  // Wilfredo Espinoza: Cara Sucia (#6)
  'wespinozah@flynet.sv': [
    { id: '6', uuid: 'f9482631-2c57-4a2e-87e1-325dcc05e7f4', name: 'Sucursal Cara Sucia', code: 'CS', city: 'Cara Sucia', isPrimary: true }
  ],
  'wespinozah': [
    { id: '6', uuid: 'f9482631-2c57-4a2e-87e1-325dcc05e7f4', name: 'Sucursal Cara Sucia', code: 'CS', city: 'Cara Sucia', isPrimary: true }
  ],
  '173': [
    { id: '6', uuid: 'f9482631-2c57-4a2e-87e1-325dcc05e7f4', name: 'Sucursal Cara Sucia', code: 'CS', city: 'Cara Sucia', isPrimary: true }
  ],
  // Vicente Chicas: Sucursal Principal San Miguel (#1)
  'finanzas@flynetwifi.com': [
    { id: '1', uuid: '231c474d-dc93-49c1-8bfb-1c80165e9f10', name: 'Sucursal Principal', code: 'SM', city: 'San Miguel', isPrimary: true }
  ],
  'vicentechicas@flynet.sv': [
    { id: '1', uuid: '231c474d-dc93-49c1-8bfb-1c80165e9f10', name: 'Sucursal Principal', code: 'SM', city: 'San Miguel', isPrimary: true }
  ],
  'vicente.chicas@flynet.sv': [
    { id: '1', uuid: '231c474d-dc93-49c1-8bfb-1c80165e9f10', name: 'Sucursal Principal', code: 'SM', city: 'San Miguel', isPrimary: true }
  ],
  'vicentechicas': [
    { id: '1', uuid: '231c474d-dc93-49c1-8bfb-1c80165e9f10', name: 'Sucursal Principal', code: 'SM', city: 'San Miguel', isPrimary: true }
  ],
  '0e360704-8987-463d-9880-31f3d86b6c79': [
    { id: '1', uuid: '231c474d-dc93-49c1-8bfb-1c80165e9f10', name: 'Sucursal Principal', code: 'SM', city: 'San Miguel', isPrimary: true }
  ],
  '84': [
    { id: '1', uuid: '231c474d-dc93-49c1-8bfb-1c80165e9f10', name: 'Sucursal Principal', code: 'SM', city: 'San Miguel', isPrimary: true }
  ],
  'aed64fd5-4f15-4cdc-941d-eea0b40bbb11': [
    { id: '1', uuid: '231c474d-dc93-49c1-8bfb-1c80165e9f10', name: 'Sucursal Principal', code: 'SM', city: 'San Miguel', isPrimary: true }
  ]
}

function enrichUserData(
  userData: any,
  rawRoles: string[],
  rawPermissions: string[],
  rawAuthData?: any
): User {
  const mergedInputRoles = Array.from(new Set([...(userData.roles || []), ...(rawRoles || [])]))
  const mergedInputPerms = Array.from(new Set([...(userData.permissions || []), ...(rawPermissions || [])]))
  const { roles, permissions } = processUserAuth(mergedInputRoles, mergedInputPerms)
  userData.roles = roles
  userData.permissions = permissions
  userData.groups = Array.from(new Set([...(userData.groups || []), ...(rawAuthData?.groups || [])]))

  // Inyección de Scope Territorial y Office ID según SDD-0024-AUTH
  const resolvedOfficeId =
    userData.office_id ??
    userData.officeId ??
    rawAuthData?.office_id ??
    rawAuthData?.officeId ??
    null

  const resolvedOfficeName =
    userData.office_name ??
    userData.officeName ??
    rawAuthData?.office_name ??
    rawAuthData?.officeName ??
    null

  userData.office_id = resolvedOfficeId
  userData.officeId = resolvedOfficeId ?? undefined
  userData.office_name = resolvedOfficeName
  userData.officeName = resolvedOfficeName ?? undefined

  const isGlobalRole =
    roles.some((r: string) =>
      [
        'PLATFORM_SUPERADMIN',
        'SuperAdmin',
        'SoporteN2',
        'isp_super_admin',
        'isp_admin',
        'admin',
        'TENANT_OWNER',
        'SUPERADMIN',
        'Owner',
        'owner',
        'CEO',
        'CFO',
        'COO',
        'DIRECTOR',
        'GERENTE_GENERAL',
        'EJECUTIVO'
      ].includes(r)
    ) || permissions.includes('*')

  userData.territorial_scope =
    rawAuthData?.territorial_scope ||
    userData.territorial_scope ||
    (isGlobalRole ? 'all' : 'local')

  // Detección y asignación de agencias para gerentes y supervisores de sucursal
  if (!userData.assignedAgencies || userData.assignedAgencies.length === 0) {
    const emailKey = (userData.email || '').toLowerCase()
    const usernameKey = (userData.username || '').toLowerCase()
    const idKey = String(userData.id || '')

    if (BRANCH_MANAGERS_MAP[emailKey]) {
      userData.assignedAgencies = [...BRANCH_MANAGERS_MAP[emailKey]]
    } else if (BRANCH_MANAGERS_MAP[usernameKey]) {
      userData.assignedAgencies = [...BRANCH_MANAGERS_MAP[usernameKey]]
    } else if (BRANCH_MANAGERS_MAP[idKey]) {
      userData.assignedAgencies = [...BRANCH_MANAGERS_MAP[idKey]]
    } else if (emailKey.includes('graciela') || usernameKey.includes('graciela')) {
      userData.assignedAgencies = [...BRANCH_MANAGERS_MAP['gracielahenriquez@flynet.sv']]
    }
  }

  if (userData.assignedAgencies && userData.assignedAgencies.length > 0 && !userData.office_id) {
    userData.office_id = userData.assignedAgencies[0].uuid
    userData.officeId = userData.assignedAgencies[0].uuid
  }

  if (userData.assignedAgencies && userData.assignedAgencies.length > 0 && !userData.office_name) {
    userData.office_name = userData.assignedAgencies[0].name
    userData.officeName = userData.assignedAgencies[0].name
  }

  return userData
}

function processUserAuth(rawRoles: string[], rawPermissions: string[]): { roles: string[]; permissions: string[] } {
  const roles = [...rawRoles]
  const perms = new Set<string>(rawPermissions)

  for (const role of roles) {
    if (ROLE_PERMISSIONS_MAP[role]) {
      for (const p of ROLE_PERMISSIONS_MAP[role]) {
        perms.add(p)
      }
    }
  }

  return { roles, permissions: Array.from(perms) }
}

function sanitizeAndEnrichMenu(rawMenu: MenuItem[], roles: string[], permissions: string[]): MenuItem[] {
  // Superadministrador y C-Level tienen acceso absoluto a todas las secciones
  const isSuperAdmin = permissions.includes('*') || roles.some(r =>
    ['PLATFORM_SUPERADMIN', 'isp_super_admin', 'isp_admin', 'admin', 'TENANT_OWNER', 'SUPERADMIN', 'Owner', 'CEO', 'CFO', 'COO', 'DIRECTOR', 'GERENTE_GENERAL', 'EJECUTIVO', 'TEAM_LEADER', 'team_leader', 'Team Leader', 'Team_Leader', 'isp_team_leader', 'LIDER', 'LIDER_TECNICO', 'SUPERVISOR'].includes(r)
  )

  // Un usuario es estrictamente NOC SOLO si NO es Superadmin y tiene rol operativo de NOC
  const isStrictlyNoc = !isSuperAdmin && roles.some(r =>
    ['isp_noc_senior', 'NOC', 'NOC_SENIOR', 'isp_noc_l1', 'NOC_JR', 'NOC_L1'].includes(r)
  )

  // Ventas debe verse si es Superadmin, o si NO es NOC estricto y tiene permisos o roles de ventas
  const hasSales = isSuperAdmin || (!isStrictlyNoc && (
    permissions.some(p => p.startsWith('sales.') || p.startsWith('crm.')) ||
    roles.some(r => ['SALES_MANAGER', 'isp_sales_admin', 'isp_sales_rep', 'isp_sales_viewer', 'EJECUTIVA_VENTAS', 'EJECUTIVO_VENTAS', 'Ventas'].includes(r))
  ))

  // 1. Filtrar ventas si no tiene permisos o si es estrictamente NOC
  let cleanMenu = rawMenu.filter(section => {
    const id = (section.id || '').toLowerCase()
    const label = (section.label || '').toLowerCase()
    if ((id === 'ventas' || label === 'ventas') && !hasSales) {
      return false
    }
    return true
  })

  // 2. Si tiene permisos de ventas (o es superadmin) y el menú no incluyó "Ventas", inyectarlo
  if (hasSales && !cleanMenu.some(s => (s.id || '').toLowerCase() === 'ventas' || (s.label || '').toLowerCase() === 'ventas')) {
    cleanMenu.push({
      id: 'ventas',
      label: 'Ventas',
      icon: 'point_of_sale',
      permissions: ['sales.*'],
      children: [
        {
          id: 'sales-dashboard',
          label: 'Dashboard de Ventas',
          icon: 'query_stats',
          path: '/sales/dashboard',
          permissions: []
        },
        {
          id: 'sales-group',
          label: 'Ventas',
          icon: 'point_of_sale',
          path: '/sales',
          permissions: [],
          children: [
            { id: 'sales-orders', label: 'Órdenes de Venta', icon: 'shopping_cart_checkout', path: '/sales/orders', permissions: [] },
            { id: 'sales-goals', label: 'Metas', icon: 'flag', path: '/sales/goals', permissions: [] },
            { id: 'sales-subscriptions', label: 'Suscripciones', icon: 'autorenew', path: '/sales/subscriptions', permissions: [] },
            { id: 'sales-sellers', label: 'Vendedores', icon: 'group', path: '/sales/sellers', permissions: [] },
            { id: 'sales-commission-rules', label: 'Reglas de Comisión', icon: 'percent', path: '/sales/commission-rules', permissions: [] },
            { id: 'sales-commissions', label: 'Comisiones', icon: 'payments', path: '/sales/commissions', permissions: [] },
          ]
        }
      ]
    })
  }

  // 3. Para usuarios de NOC o Superadmin, asegurar que siempre tengan disponibles sus secciones operativas
  const isNoc = isStrictlyNoc || roles.some(r => ['isp_noc_senior', 'NOC', 'NOC_SENIOR', 'isp_noc_l1', 'NOC_JR', 'NOC_L1', 'isp_network_admin', 'isp_support_admin'].includes(r))
  const isAgencyManager = roles.some(r => ['AGENCY_MANAGER', 'JEFE_AGENCIA', 'Gerente de Ventas'].includes(r))

  if (isNoc || isSuperAdmin) {
    const isSenior = isSuperAdmin || roles.some(r => ['isp_noc_senior', 'NOC', 'NOC_SENIOR', 'isp_network_admin'].includes(r))

    // Operaciones (Red)
    const opSection = cleanMenu.find(s => (s.id || '').toLowerCase() === 'operaciones')
    if (!opSection) {
      cleanMenu.push({
        id: 'operaciones',
        label: 'Operaciones',
        icon: 'lan',
        permissions: [],
        children: [
          {
            id: 'red',
            label: 'Red',
            icon: 'lan',
            path: '/network',
            permissions: [],
            children: [
              { id: 'network-ipam', label: 'IPAM', icon: 'dns', path: '/network/ipam', permissions: [] },
              { id: 'network-equipment', label: 'Equipos de Red', icon: 'router', path: '/network/equipment', permissions: [] },
              { id: 'network-pppoe', label: 'Sesiones PPPoE', icon: 'settings_ethernet', path: '/network/pppoe', permissions: [] },
              { id: 'network-cpes', label: 'CPEs', icon: 'hub', path: '/network/cpes', permissions: [] },
              { id: 'network-nodes', label: 'Nodos', icon: 'cell_tower', path: '/network/nodes', permissions: [] },
              { id: 'network-auth-servers', label: 'Auth Servers', icon: 'security', path: '/network/auth-servers', permissions: [] },
              { id: 'network-nat-servers', label: 'Servidores NAT', icon: 'dns', path: '/network/nat-servers', permissions: [] },
              { id: 'network-devices', label: 'Dispositivos', icon: 'devices_other', path: '/network/devices', permissions: [] },
              { id: 'network-fiber-census', label: 'Censo de Fibra', icon: 'cable', path: '/network/fiber-census', permissions: [] },
              { id: 'network-naps', label: 'Cajas NAP', icon: 'hub', path: '/network/naps', permissions: [] },
              { id: 'network-gpon-health', label: 'Salud Óptica PON', icon: 'insights', path: '/network/gpon-health', permissions: [] },
            ]
          }
        ]
      })
    } else {
      const redChild = opSection.children?.find(c => (c.id || '').toLowerCase() === 'red')
      if (redChild && redChild.children) {
        if (!redChild.children.some(c => c.path === '/network/fiber-census')) {
          redChild.children.push({
            id: 'network-fiber-census',
            label: 'Censo de Fibra',
            icon: 'cable',
            path: '/network/fiber-census',
            permissions: []
          })
        }
        if (!redChild.children.some(c => c.path === '/network/naps')) {
          redChild.children.push({
            id: 'network-naps',
            label: 'Cajas NAP',
            icon: 'hub',
            path: '/network/naps',
            permissions: []
          })
        }
        if (!redChild.children.some(c => c.path === '/network/gpon-health')) {
          redChild.children.push({
            id: 'network-gpon-health',
            label: 'Salud Óptica PON',
            icon: 'insights',
            path: '/network/gpon-health',
            permissions: []
          })
        }
      }
    }

    // OTT (solo senior o IPTV o Superadmin)
    if (isSenior && !cleanMenu.some(s => (s.id || '').toLowerCase() === 'ott')) {
      cleanMenu.push({
        id: 'ott',
        label: 'OTT',
        icon: 'live_tv',
        permissions: [],
        children: [
          { id: 'netplay-dashboard', label: 'Dashboard OTT', icon: 'live_tv', path: '/netplay/dashboard', permissions: [] },
          { id: 'netplay-customers', label: 'Clientes OTT', icon: 'groups', path: '/netplay/customers', permissions: [] },
        ]
      })
    }
  }

  // 4. Helpdesk & Omnicanal (Superadmin, NOC, Jefes de Agencia o roles Helpdesk/WhatsApp)
  const hasHelpdesk = isSuperAdmin || isNoc || isAgencyManager || roles.some(r => [
    'HELPDESK_MANAGER', 'HELPDESK_AGENT', 'SUPPORT_MANAGER',
    'isp_whatsapp_admin', 'isp_whatsapp_operator',
    'isp_support_admin', 'isp_support_agent', 'isp_support_viewer'
  ].includes(r)) || permissions.some(p => p.startsWith('helpdesk.') || p.startsWith('support.'))

  if (hasHelpdesk && !cleanMenu.some(s => (s.id || '').toLowerCase() === 'helpdesk')) {
    cleanMenu.push({
      id: 'helpdesk',
      label: 'Helpdesk',
      icon: 'support_agent',
      permissions: [],
      children: [
        { id: 'helpdesk-tickets', label: 'Tickets', icon: 'confirmation_number', path: '/helpdesk/tickets', permissions: [] },
        { id: 'helpdesk-console', label: 'Consola Omnicanal', icon: 'headset_mic', path: '/helpdesk/console', permissions: [] },
        { id: 'helpdesk-templates', label: 'Plantillas', icon: 'quick_phrases', path: '/helpdesk/templates', permissions: [] },
      ]
    })
  }

  // 5. Clientes (Directorio de Clientes)
  const hasCustomers = isSuperAdmin || isNoc || isAgencyManager || permissions.some(p => p.startsWith('customers.') || p === '*') || roles.some(r => ['isp_customer_manager', 'BILLING_MANAGER'].includes(r))
  if (hasCustomers && !cleanMenu.some(s => (s.id || '').toLowerCase() === 'clientes')) {
    cleanMenu.push({
      id: 'clientes',
      label: 'Clientes',
      icon: 'groups',
      permissions: [],
      children: [
        { id: 'customers-dir', label: 'Directorio de Clientes', icon: 'groups', path: '/customers', permissions: [] },
      ]
    })
  }

  // 6. Tareas
  const hasTasks = isSuperAdmin || isNoc || isAgencyManager || permissions.some(p => p.startsWith('tasks.')) || roles.some(r => ['isp_tasks_admin'].includes(r))
  if (hasTasks && !cleanMenu.some(s => (s.id || '').toLowerCase() === 'tareas')) {
    cleanMenu.push({
      id: 'tareas',
      label: 'Tareas',
      icon: 'task',
      permissions: [],
      children: [
        { id: 'tasks-list', label: 'Lista de Tareas', icon: 'list', path: '/tasks', permissions: [] },
        { id: 'tasks-calendar', label: 'Calendario', icon: 'calendar_month', path: '/tasks/calendar', permissions: [] },
        { id: 'tasks-agenda', label: 'Mi Agenda', icon: 'view_agenda', path: '/tasks/agenda', permissions: [] },
      ]
    })
  }

  // 7. ERP - Filtrar estrictamente por permisos/roles ERP (evitar que salga a técnicos/NOC/ventas)
  const isFieldTechOnly = !isSuperAdmin && roles.some(r => ['isp_fiber_technician', 'TecnicoCampo'].includes(r))
  const hasErpWildcard = isSuperAdmin || permissions.includes('erp.*') || roles.some(r =>
    ['ERP_MANAGER', 'BILLING_MANAGER', 'FINANCIAL_MANAGER', 'Contador', 'isp_erp_admin'].includes(r)
  )
  const hasErpCatalog = hasErpWildcard || permissions.some(p => p.startsWith('erp.catalog.') || p.startsWith('erp.products.'))
  const hasErpWarehouse = hasErpWildcard || isAgencyManager || permissions.some(p =>
    p.startsWith('erp.warehouse.') || p.startsWith('erp.warehouses.') || p.startsWith('erp.inventory.') || p.startsWith('erp.equipment.')
  ) || roles.some(r => ['isp_erp_operator', 'isp_erp_viewer'].includes(r))
  const hasErpAccounting = hasErpWildcard || permissions.some(p => p.startsWith('erp.accounting.'))
  const hasErp = !isStrictlyNoc && !isFieldTechOnly && (
    hasErpWildcard || hasErpCatalog || hasErpWarehouse || hasErpAccounting ||
    permissions.some(p => p.startsWith('erp.') && p !== 'erp.inventory.assigned_read' && p !== 'erp:inventory:assigned_read')
  )

  if (!hasErp) {
    cleanMenu = cleanMenu.filter(section => {
      const id = (section.id || '').toLowerCase()
      const label = (section.label || '').toLowerCase()
      return id !== 'erp' && label !== 'erp'
    })
  } else {
    let erpSection = cleanMenu.find(s => (s.id || '').toLowerCase() === 'erp' || (s.label || '').toLowerCase() === 'erp')
    if (!erpSection) {
      erpSection = {
        id: 'erp',
        label: 'ERP',
        icon: 'business',
        permissions: [],
        children: []
      }
      cleanMenu.push(erpSection)
    }

    if (erpSection && erpSection.children) {
      // Si el BFF inyectó "Modelos IA" sin permiso (legacy MenuSection), limpiarlo si el usuario no tiene catálogo ERP
      if (!hasErpCatalog) {
        erpSection.children = erpSection.children.filter(item =>
          item.path !== '/erp/catalog/ai-models' &&
          item.path !== '/erp/ai-models' &&
          item.id !== 'erp-ai-models' &&
          !(item.label || '').toLowerCase().includes('modelos ia')
        )
      }

      const erpItems: Array<MenuItem & { allowed: boolean }> = [
        {
          id: 'erp-warehouses',
          label: 'Almacenes',
          icon: 'warehouse',
          path: '/erp/warehouse',
          permissions: [],
          allowed: hasErpWarehouse
        },
        {
          id: 'erp-inventory',
          label: 'Inventario',
          icon: 'inventory_2',
          path: '/erp/inventory',
          permissions: [],
          allowed: hasErpWarehouse
        },
        {
          id: 'erp-accounting-journals',
          label: 'Pólizas Contables',
          icon: 'menu_book',
          path: '/erp/accounting/journals',
          permissions: [],
          allowed: hasErpAccounting
        }
      ]

      for (const item of erpItems) {
        if (!item.allowed) continue
        const exists = erpSection.children.some(c =>
          c.id === item.id ||
          c.path === item.path ||
          (c.label || '').toLowerCase() === item.label.toLowerCase()
        )
        if (!exists) {
          erpSection.children.push({
            id: item.id,
            label: item.label,
            icon: item.icon,
            path: item.path,
            permissions: item.permissions
          })
        }
      }

      if (hasErpCatalog) {
        const hasAiModelsDirect = erpSection.children.some(item =>
          item.path === '/erp/catalog/ai-models' ||
          item.path === '/erp/ai-models' ||
          item.id === 'erp-ai-models' ||
          (item.label || '').toLowerCase().includes('modelos ia')
        )
        if (!hasAiModelsDirect) {
          erpSection.children.unshift({
            id: 'erp-ai-models',
            label: 'Modelos IA & Inferencia',
            icon: 'psychology',
            path: '/erp/catalog/ai-models',
            permissions: []
          })
        }

        const commercialCatalog = erpSection.children.find(c =>
          c.path === '/erp/commercial' ||
          (c.label || '').toLowerCase().includes('comercial') ||
          (c.id || '').toLowerCase().includes('comercial')
        )
        if (commercialCatalog) {
          if (!commercialCatalog.children) {
            commercialCatalog.children = []
          }
          const hasAiModels = commercialCatalog.children.some(item =>
            item.path === '/erp/catalog/ai-models' ||
            (item.label || '').toLowerCase().includes('modelos ia') ||
            item.id === 'erp-ai-models-sub'
          )
          if (!hasAiModels) {
            commercialCatalog.children.push({
              id: 'erp-ai-models-sub',
              label: 'Modelos IA',
              icon: 'psychology',
              path: '/erp/catalog/ai-models',
              permissions: []
            })
          }
        }
      }

      if (erpSection.children.length === 0) {
        cleanMenu = cleanMenu.filter(s => s !== erpSection)
      }
    }
  }

  return cleanMenu
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const menu = ref<MenuItem[]>([])
  const csrfToken = ref<string | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Al usar cookies, la autenticación depende de que tengamos los datos del usuario.
  const isAuthenticated = computed(() => !!user.value)
  
  const hasRole = (role: string) => user.value?.roles.includes(role) ?? false
  const hasPermission = (permission: string) => {
    if (!user.value) return false
    if (!permission) return true
    const reqNorm = permission.trim().replace(/:/g, '.')
    if (!reqNorm) return true
    const reqParts = reqNorm.split('.')

    return user.value.permissions.some(rawPerm => {
      const pNorm = rawPerm.trim().replace(/:/g, '.')
      if (!pNorm) return false
      if (pNorm === '*' || pNorm === reqNorm) return true

      if (pNorm.endsWith('.*')) {
        const prefix = pNorm.slice(0, -2)
        return reqNorm === prefix || reqNorm.startsWith(prefix + '.')
      }

      const pParts = pNorm.split('.')
      if (pParts.length !== reqParts.length) return false
      return pParts.every((seg, idx) => seg === '*' || seg === reqParts[idx])
    })
  }

  const login = async (username: string, password: string) => {
    isLoading.value = true
    error.value = null
    
    try {
      const response = await api.post('/api/auth/login', { username, password })
      
      const userData = response.data.user
      if (userData) {
        user.value = enrichUserData(
          userData,
          response.data.authorization?.roles || [],
          response.data.authorization?.permissions || [],
          response.data.authorization || response.data
        )
      }
      
      menu.value = sanitizeAndEnrichMenu(
        response.data.ui?.menu || [],
        user.value?.roles || [],
        user.value?.permissions || []
      )
      
      if (response.data.csrf_token) {
        csrfToken.value = response.data.csrf_token
        api.defaults.headers.common['X-CSRF-Token'] = csrfToken.value
      }

      if (response.data.session?.expires_at) {
        scheduleSilentRefresh(response.data.session.expires_at)
      }
      return true
    } catch (err: any) {
      const serverMessage =
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        (typeof err.response?.data?.error === 'string' ? err.response?.data?.error : null)

      const status = err.response?.status

      if (status === 401 || err.response?.data?.error?.code === 'INVALID_CREDENTIALS') {
        error.value = 'Usuario o contraseña incorrectos. Por favor verifique sus datos e intente nuevamente.'
      } else if (status === 403) {
        error.value = 'Acceso denegado. No posee permisos para ingresar al sistema.'
      } else if (status === 429) {
        error.value = 'Demasiados intentos fallidos. Por favor espere unos momentos antes de reintentar.'
      } else if (status && status >= 500) {
        error.value = 'El servicio de autenticación no está disponible en este momento. Intente de nuevo más tarde.'
      } else if (serverMessage && !serverMessage.toLowerCase().includes('status code')) {
        error.value = serverMessage
      } else if (!err.response) {
        error.value = 'No se pudo conectar con el servidor. Verifique su conexión de red.'
      } else {
        error.value = 'Error al iniciar sesión. Por favor verifique sus datos e intente nuevamente.'
      }
      return false
    } finally {
      isLoading.value = false
    }
  }

  const logout = async () => {
    clearSilentRefresh()
    try {
      await api.post('/api/auth/logout') // El backend limpiará la cookie httpOnly
    } catch (e) {
      // Ignorar errores de logout
    }
    user.value = null
    menu.value = []
    csrfToken.value = null
    delete api.defaults.headers.common['X-CSRF-Token']
  }

  const initialize = async () => {
    try {
      // Al hacer get('/me'), el navegador inyecta automáticamente la cookie de la sesión
      const response = await api.get('/api/auth/me')
      
      const userData = response.data.user || response.data // Fallback por si `/me` devuelve el usuario directo
      if (userData) {
        user.value = enrichUserData(
          userData,
          response.data.authorization?.roles || userData.roles || [],
          response.data.authorization?.permissions || userData.permissions || [],
          response.data.authorization || response.data
        )
      }
      
      menu.value = sanitizeAndEnrichMenu(
        response.data.ui?.menu || [],
        user.value?.roles || [],
        user.value?.permissions || []
      )

      // Iniciar el ciclo de refresco silencioso en segundo plano
      await refreshAuthSession()
    } catch {
      user.value = null
      menu.value = []
      clearSilentRefresh()
    }
  }

  const isAgencyManager = computed(() => {
    if (!user.value) return false
    const roles = user.value.roles || []
    const emailKey = (user.value.email || '').toLowerCase()
    const usernameKey = (user.value.username || '').toLowerCase()

    return (
      roles.some(r => [
        'AGENCY_MANAGER',
        'JEFE_AGENCIA',
        'BRANCH_MANAGER',
        'BRANCH_SUPERVISOR',
        'SUPERVISOR_AGENCIA',
        'Gerente de Ventas'
      ].includes(r)) ||
      (user.value.assignedAgencies && user.value.assignedAgencies.length > 0 && !hasRole('PLATFORM_SUPERADMIN') && !hasRole('TENANT_OWNER') && !hasRole('TENANT_ADMIN')) ||
      Object.keys(BRANCH_MANAGERS_MAP).includes(emailKey) ||
      Object.keys(BRANCH_MANAGERS_MAP).includes(usernameKey)
    )
  })

  const isExecutive = computed(() => {
    if (!user.value) return false
    return user.value.roles.some(r =>
      ['CEO', 'CFO', 'COO', 'DIRECTOR', 'GERENTE_GENERAL', 'EJECUTIVO', 'FINANCIAL_MANAGER', 'Contador'].includes(r)
    )
  })

  const isSales = computed(() => {
    if (!user.value) return false
    return user.value.roles.some(r =>
      ['SALES_REP', 'EJECUTIVA_VENTAS', 'EJECUTIVO_VENTAS', 'isp_sales_rep', 'isp_sales_admin', 'SALES_MANAGER', 'Ventas'].includes(r)
    )
  })

  const isSalesSanMiguel = computed(() => {
    if (!isSales.value || !user.value) return false
    const offices = user.value.assignedAgencies?.map(a => String(a.id)) || []
    if (user.value.officeId) offices.push(String(user.value.officeId))
    return (
      offices.includes('1') ||
      (user.value.assignedAgencies?.some(a => a.code === 'SM' || a.name.toLowerCase().includes('san miguel')) ?? false)
    )
  })

  const assignedAgencies = computed<AssignedAgency[]>(() => {
    return user.value?.assignedAgencies || []
  })

  const territorialScope = computed(() => user.value?.territorial_scope || 'local')
  const officeId = computed(() => user.value?.office_id ?? user.value?.officeId ?? null)
  const officeName = computed(() => user.value?.office_name ?? user.value?.officeName ?? null)

  return {
    user,
    menu,
    csrfToken,
    isLoading,
    error,
    isAuthenticated,
    isAgencyManager,
    isExecutive,
    isSales,
    isSalesSanMiguel,
    assignedAgencies,
    territorialScope,
    officeId,
    officeName,
    hasRole,
    hasPermission,
    login,
    logout,
    initialize
  }
})
